import React, { useState } from 'react';
import api from '../../../services/api';

const VehicleCreateTest = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: 'Toyota Land Cruiser',
    registration_number: 'KCB 123A',
    type: 'SUV',
    capacity: 7,
    description: 'Luxury SUV for safari tours',
    status: 'available',
    model: 'Land Cruiser',
    model_year: 2023,
    rate_per_day: 15000,
    rate_per_km: 100,
    make: 'Toyota',
    is_active: true,
    features: ['Air Conditioning', '4x4', 'Roof Rack']
  });
  const [newFeature, setNewFeature] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const createVehicle = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    
    try {
      console.log('Creating vehicle with data:', formData);
      
      // Create FormData object
      const vehicleFormData = new FormData();
      
      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (formData[key] !== undefined && formData[key] !== null) {
          // Handle boolean values properly
          if (typeof formData[key] === 'boolean') {
            vehicleFormData.append(key, formData[key] ? '1' : '0');
          } else if (key === 'features' && Array.isArray(formData[key])) {
            // Ensure features is properly formatted as JSON string
            const featuresJson = JSON.stringify(formData[key]);
            console.log('Features JSON:', featuresJson);
            vehicleFormData.append(key, featuresJson);
          } else if (Array.isArray(formData[key])) {
            // Convert other arrays to JSON strings
            vehicleFormData.append(key, JSON.stringify(formData[key]));
          } else {
            vehicleFormData.append(key, formData[key]);
          }
        }
      });
      
      // Log the FormData contents (for debugging)
      console.log('FormData entries:');
      for (let pair of vehicleFormData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      // Make the API call
      const response = await api.post('/admin/vehicles', vehicleFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('API Response:', response);
      setResult(response.data);
    } catch (err) {
      console.error('Error creating vehicle:', err);
      setError(err.response?.data || err.message || 'Failed to create vehicle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Vehicle Creation Test</h1>
      
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Create a Test Vehicle</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {Object.keys(formData).map(key => {
            if (key === 'features') return null; // Skip features, we'll handle it separately
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </label>
                {key === 'is_active' ? (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name={key}
                      checked={formData[key]}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {formData[key] ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ) : (
                  <input
                    type={key.includes('model_year') || key.includes('capacity') || key.includes('rate') ? 'number' : 'text'}
                    name={key}
                    value={formData[key]}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                )}
              </div>
            );
          })}
        </div>
        
        {/* Features Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Features
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.features.map((feature, index) => (
              <div key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center">
                <span>{feature}</span>
                <button 
                  type="button"
                  onClick={() => handleRemoveFeature(index)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
          <div className="flex">
            <input
              type="text"
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              placeholder="Add a feature"
              className="flex-grow rounded-l-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
            />
            <button
              type="button"
              onClick={handleAddFeature}
              className="bg-blue-500 text-white px-4 py-2 rounded-r-md hover:bg-blue-600"
            >
              Add
            </button>
          </div>
        </div>
        
        <button
          onClick={createVehicle}
          disabled={loading}
          className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Test Vehicle'}
        </button>
      </div>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p className="font-bold">Error</p>
          <pre className="mt-2 text-sm overflow-auto max-h-60">
            {typeof error === 'object' ? JSON.stringify(error, null, 2) : error}
          </pre>
        </div>
      )}
      
      {result && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
          <p className="font-bold">Success</p>
          <pre className="mt-2 text-sm overflow-auto max-h-60">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
      
      <div className="bg-gray-100 p-4 rounded-lg">
        <h2 className="text-lg font-semibold mb-2">API Request Details</h2>
        <p className="text-sm text-gray-600 mb-2">
          Open your browser's developer console to see detailed API request and response logs.
        </p>
        <pre className="bg-gray-800 text-green-400 p-4 rounded text-xs overflow-auto max-h-60">
{`POST http://localhost:8000/api/v1/admin/vehicles
Headers:
- Authorization: Bearer [your-token]
- Content-Type: multipart/form-data

FormData:
- name: Toyota Land Cruiser
- registration_number: KCB 123A
- type: SUV
- capacity: 7
- description: Luxury SUV for safari tours
- status: available
- model: Land Cruiser
- model_year: 2023
- rate_per_day: 15000
- rate_per_km: 100
- make: Toyota
- is_active: true
- features: ["Air Conditioning", "4x4", "Roof Rack"]`}
        </pre>
      </div>
    </div>
  );
};

export default VehicleCreateTest;
